import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** `sending` (PD-17517): claimed by the worker emailing it right now, so no other worker sends it too. */
export type ReportScheduleDeliveryStatusModel = 'pending' | 'sending' | 'sent' | 'failed' | 'bounced' | 'skipped';

/**
 * PD-17398 — one recipient of one run. Unique per (run, recipient), which is
 * what makes a retry send only to those not yet delivered. The email exactly
 * as sent is kept (`html`) for History's "View", 90 days.
 */
@Index('idx_report_schedule_delivery_run_recipient', ['fkReportScheduleRunId', 'recipientKey'], { unique: true })
@Index('idx_report_schedule_delivery_message', ['messageId'])
@Index('idx_report_schedule_delivery_token', ['optOutToken'])
// auditable: false - send bookkeeping, and the stored email copy is large.
@Entity('report_schedule_delivery', {}, { auditable: false })
export class ReportScheduleDeliveryEntity extends BaseEntity {
	@Column('int', { name: 'fk_report_schedule_run__id', unsigned: true })
	public fkReportScheduleRunId!: number;

	/** Denormalized for the opt-out link and History. */
	@Column('int', { name: 'fk_report_schedule__id', unsigned: true })
	public fkReportScheduleId!: number;

	/** `user:<id>` or `email:<address>`. */
	@Column('varchar', { name: 'recipientKey', length: 300 })
	public recipientKey!: string;

	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	@Column('varchar', { name: 'email', length: 255 })
	public email!: string;

	@Column('varchar', { name: 'status', length: 20, default: 'pending' })
	public status!: ReportScheduleDeliveryStatusModel;

	@Column('varchar', { name: 'messageId', length: 255, nullable: true })
	public messageId!: string | null;

	@Column('datetime', { name: 'sentAt', nullable: true })
	public sentAt!: Date | null;

	/** Why it failed or was skipped (e.g. "cannot open Sales Comparison"). */
	@Column('varchar', { name: 'note', length: 500, nullable: true })
	public note!: string | null;

	/** Random token for this recipient's "Stop sending me this" link. */
	@Column('varchar', { name: 'optOutToken', length: 64, nullable: true })
	public optOutToken!: string | null;

	/**
	 * PD-17476 — which restricted outputs the copy as sent holds, so History
	 * shows it only to people who may see them: a comma list of `gp` and
	 * `netCost`, '' for neither. Null on deliveries sent before it was
	 * recorded, which are treated as holding both.
	 */
	@Column('varchar', { name: 'restrictedOutputs', length: 40, nullable: true })
	public restrictedOutputs!: string | null;

	/** The email body as sent, for History; cleared after 90 days. */
	@Column('longtext', { name: 'html', nullable: true })
	public html!: string | null;
}
